import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAnalytics } from '@vaptmenu/analytics-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { ordersKeys } from '@/features/orders/hooks/useOrders';
import type { ApiError } from '@/shared/lib/problem-details';
import {
  activatePrinting,
  confirmPairing,
  createNetworkPrinter,
  createStation,
  deactivatePrinting,
  deletePrinter,
  deleteStation,
  getAgentRelease,
  getAgents,
  getAssignments,
  getOrderPrintJobs,
  getPairingRequest,
  getPrinters,
  getPrintingSetup,
  getPrintingStatus,
  getRoutes,
  getStations,
  getTicketPreview,
  pausePrinting,
  renameAgent,
  reprintOrder,
  requestAgentUpdate,
  resumePrinting,
  revokeAgent,
  saveAssignments,
  saveCategoryRoutes,
  saveProductRoute,
  sendTestPrint,
  testPrinterConnection,
  updatePrinter,
  updateStation,
} from '../api/printing.api';
import { errorStatus, isPrintingError, PRINTING_ERRORS } from '../lib/printing-errors';
import type {
  ActivateSetupResponse,
  AgentRelease,
  AgentUpdateMode,
  AgentUpdateRequestResponse,
  CategoryRoutesBatchRequest,
  ConnectionTestResponse,
  CreateNetworkPrinterRequest,
  DeactivateSetupResponse,
  OrderPrintJobs,
  PairingRequestView,
  PauseStateResponse,
  PrintAgent,
  PrintAgentsResponse,
  Printer,
  PrinterTransport,
  PrinterWidth,
  PrintersResponse,
  PrintingAssignments,
  PrintingRoutes,
  PrintingSetup,
  PrintingStatus,
  ProductionDestination,
  ProductionStation,
  ReprintResponse,
  SaveAssignmentsRequest,
  StationMoveTarget,
  StationsResponse,
  TestPrintResponse,
  TicketChoice,
  TicketKind,
  UpdatePrinterRequest,
} from '../types/printing.types';

/* ==========================================================================
   Chaves de cache — por estabelecimento

   Tudo o que muda com a configuração mora sob `['printing', tenantId]`: é a
   raiz que os eventos de tempo real, o Vapt IA e as gravações invalidam. As
   prévias e a leitura do código de pareamento ficam fora dela de propósito:
   a prévia não muda com a configuração (só com a largura), e reler o código
   depois de confirmar devolveria "código não reconhecido" na tela de sucesso.
   ========================================================================== */

export const printingKeys = {
  root: (tenantId: string) => ['printing', tenantId] as const,
  status: (tenantId: string) => [...printingKeys.root(tenantId), 'status'] as const,
  setup: (tenantId: string) => [...printingKeys.root(tenantId), 'setup'] as const,
  stations: (tenantId: string) => [...printingKeys.root(tenantId), 'stations'] as const,
  routes: (tenantId: string) => [...printingKeys.root(tenantId), 'routes'] as const,
  agents: (tenantId: string) => [...printingKeys.root(tenantId), 'agents'] as const,
  /** Versão e atualização dos computadores (change print-agent): relida em todo `printing.*`. */
  agentRelease: (tenantId: string) => [...printingKeys.root(tenantId), 'agent-release'] as const,
  printers: (tenantId: string) => [...printingKeys.root(tenantId), 'printers'] as const,
  assignments: (tenantId: string) => [...printingKeys.root(tenantId), 'assignments'] as const,
  orderJobsRoot: (tenantId: string) => [...printingKeys.root(tenantId), 'order-jobs'] as const,
  orderJobs: (tenantId: string, orderId: string) =>
    [...printingKeys.orderJobsRoot(tenantId), orderId] as const,
  preview: (tenantId: string, kind: TicketKind, width: PrinterWidth, stationId: string | null) =>
    ['printing-preview', tenantId, kind, width, stationId] as const,
  pairing: (tenantId: string, code: string) => ['printing-pairing', tenantId, code] as const,
};

/** Intervalo da revalidação do estado (faixa, aba e selos). */
export const PRINTING_STATUS_POLL_MS = 60_000;

function currentTenantId(): string {
  return useAuthStore.getState().selectedTenant?.id ?? '';
}

function useTenantId(): string {
  return useAuthStore((state) => state.selectedTenant?.id ?? '');
}

/**
 * 4xx da impressão é resposta, não falha passageira: 404 fora do piloto, 403
 * de assinatura. Só erro de servidor ou de rede tenta de novo, uma vez.
 */
function printingRetry(failureCount: number, error: ApiError): boolean {
  const status = errorStatus(error) ?? 0;
  return (status === 0 || status >= 500) && failureCount < 1;
}

/** Revalida tudo o que depende da configuração da impressão. */
export function invalidatePrinting(queryClient: QueryClient, tenantId = currentTenantId()): void {
  if (!tenantId) return;
  void queryClient.invalidateQueries({ queryKey: printingKeys.root(tenantId) });
}

/* ==========================================================================
   Leituras
   ========================================================================== */

export function usePrintingStatus(enabled = true) {
  const tenantId = useTenantId();
  return useQuery<PrintingStatus, ApiError>({
    queryKey: printingKeys.status(tenantId),
    queryFn: getPrintingStatus,
    enabled: enabled && !!tenantId,
    refetchInterval: PRINTING_STATUS_POLL_MS,
    retry: printingRetry,
  });
}

/** `pollMs`: o assistente relê enquanto espera o computador confirmar. */
export function usePrintingSetup(enabled = true, pollMs?: number) {
  const tenantId = useTenantId();
  return useQuery<PrintingSetup, ApiError>({
    queryKey: printingKeys.setup(tenantId),
    queryFn: getPrintingSetup,
    enabled: enabled && !!tenantId,
    retry: printingRetry,
    refetchInterval: pollMs,
  });
}

export function useStations(enabled = true) {
  const tenantId = useTenantId();
  return useQuery<StationsResponse, ApiError>({
    queryKey: printingKeys.stations(tenantId),
    queryFn: getStations,
    enabled: enabled && !!tenantId,
    retry: printingRetry,
  });
}

export function usePrintingRoutes(enabled = true) {
  const tenantId = useTenantId();
  return useQuery<PrintingRoutes, ApiError>({
    queryKey: printingKeys.routes(tenantId),
    queryFn: getRoutes,
    enabled: enabled && !!tenantId,
    retry: printingRetry,
  });
}

export function usePrintAgents(enabled = true) {
  const tenantId = useTenantId();
  return useQuery<PrintAgentsResponse, ApiError>({
    queryKey: printingKeys.agents(tenantId),
    queryFn: getAgents,
    enabled: enabled && !!tenantId,
    retry: printingRetry,
  });
}

/**
 * Versão, nível e pedido de atualização de cada computador ativo (`settings.read`).
 * Relida na abertura, em todo evento `printing.*` da conexão compartilhada (inclusive
 * `printing.agent_update`) e depois de cada gravação da impressão.
 */
export function useAgentRelease(enabled = true) {
  const tenantId = useTenantId();
  return useQuery<AgentRelease, ApiError>({
    queryKey: printingKeys.agentRelease(tenantId),
    queryFn: getAgentRelease,
    enabled: enabled && !!tenantId,
    retry: printingRetry,
  });
}

export function usePrinters(enabled = true) {
  const tenantId = useTenantId();
  return useQuery<PrintersResponse, ApiError>({
    queryKey: printingKeys.printers(tenantId),
    queryFn: getPrinters,
    enabled: enabled && !!tenantId,
    retry: printingRetry,
  });
}

export function usePrintingAssignments(enabled = true) {
  const tenantId = useTenantId();
  return useQuery<PrintingAssignments, ApiError>({
    queryKey: printingKeys.assignments(tenantId),
    queryFn: getAssignments,
    enabled: enabled && !!tenantId,
    retry: printingRetry,
  });
}

export function useOrderPrintJobs(orderId: string | null, enabled = true) {
  const tenantId = useTenantId();
  return useQuery<OrderPrintJobs, ApiError>({
    queryKey: printingKeys.orderJobs(tenantId, orderId ?? ''),
    queryFn: () => getOrderPrintJobs(orderId!),
    enabled: enabled && !!tenantId && !!orderId,
    retry: printingRetry,
  });
}

export function usePairingRequest(code: string, enabled = true) {
  const tenantId = useTenantId();
  return useQuery<PairingRequestView, ApiError>({
    queryKey: printingKeys.pairing(tenantId, code),
    queryFn: () => getPairingRequest(code),
    enabled: enabled && !!tenantId && !!code,
    retry: false,
    staleTime: Infinity,
  });
}

/** Prévia com o pedido de exemplo, como Blob (a rota exige o token). */
export function useTicketPreview(
  kind: TicketKind,
  width: PrinterWidth,
  stationId: string | null,
  enabled = true,
) {
  const tenantId = useTenantId();
  return useQuery<Blob, ApiError>({
    queryKey: printingKeys.preview(tenantId, kind, width, stationId),
    queryFn: () => getTicketPreview(kind, width, stationId),
    enabled: enabled && !!tenantId,
    retry: printingRetry,
    staleTime: 10 * 60_000,
  });
}

/* ==========================================================================
   Gravações

   Cada gravação revalida a raiz da impressão também no erro: um 409
   `printing.platform_disabled` quer dizer que a tela está atrás do servidor,
   e a aba precisa reler o estado para passar ao modo leitura. Os eventos de
   analytics saem só no 2xx, e só quando a resposta diz que algo mudou.
   ========================================================================== */

function usePrintingMutationBase() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const settle = () => invalidatePrinting(queryClient);
  return { queryClient, track, settle };
}

export function useActivatePrinting() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<ActivateSetupResponse, ApiError, { printerCount: number }>({
    mutationFn: () => activatePrinting(),
    onSuccess: (result, { printerCount }) => {
      if (result.changed) {
        track('panel.printing_setup_completed', {
          station_count: result.setup.progress.stationCount,
          printer_count: printerCount,
          reactivated: result.reactivated,
        });
      }
    },
    onSettled: settle,
  });
}

export function usePausePrinting() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<PauseStateResponse, ApiError, { paused: boolean }>({
    mutationFn: ({ paused }) => (paused ? pausePrinting() : resumePrinting()),
    onSuccess: (result) => {
      if (result.changed) track('panel.printing_pause_changed', { paused: result.paused });
    },
    onSettled: settle,
  });
}

export function useDeactivatePrinting() {
  const { queryClient, track, settle } = usePrintingMutationBase();
  return useMutation<DeactivateSetupResponse, ApiError, void>({
    mutationFn: () => deactivatePrinting(),
    onSuccess: (result) => {
      if (result.changed) {
        track('panel.printing_deactivated', { revoked_agent_count: result.revokedAgentCount });
      }
      // A faixa e os selos somem para os pedidos novos: a fila também relê.
      const tenantId = currentTenantId();
      if (tenantId) void queryClient.invalidateQueries({ queryKey: ordersKeys.root(tenantId) });
    },
    onSettled: settle,
  });
}

export function useSaveStation() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<ProductionStation, ApiError, { id?: string; name: string; isDefault?: boolean }>({
    mutationFn: ({ id, name, isDefault }) =>
      id ? updateStation(id, { name, isDefault }) : createStation({ name, isDefault }),
    onSuccess: (station, { id }) => {
      track('panel.production_station_saved', {
        action: id ? 'updated' : 'created',
        is_default: station.isDefault,
      });
    },
    onSettled: settle,
  });
}

export function useDeleteStation() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<void, ApiError, { station: ProductionStation; moveTo?: StationMoveTarget }>({
    mutationFn: ({ station, moveTo }) => deleteStation(station.id, moveTo),
    onSuccess: (_result, { station }) => {
      track('panel.production_station_saved', { action: 'deleted', is_default: station.isDefault });
    },
    onSettled: settle,
  });
}

export function useSaveCategoryRoutes() {
  const { settle } = usePrintingMutationBase();
  return useMutation<void, ApiError, CategoryRoutesBatchRequest>({
    mutationFn: saveCategoryRoutes,
    onSettled: settle,
  });
}

export function useSaveProductRoute() {
  const { settle } = usePrintingMutationBase();
  return useMutation<void, ApiError, { productId: string; destination: ProductionDestination }>({
    mutationFn: ({ productId, destination }) => saveProductRoute(productId, destination),
    onSettled: settle,
  });
}

export function useRenameAgent() {
  const { settle } = usePrintingMutationBase();
  return useMutation<PrintAgent, ApiError, { id: string; name: string }>({
    mutationFn: ({ id, name }) => renameAgent(id, name),
    onSettled: settle,
  });
}

export function useRevokeAgent() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<PrintAgent, ApiError, string>({
    mutationFn: revokeAgent,
    onSuccess: () => track('panel.print_agent_revoked'),
    onSettled: settle,
  });
}

export function useConfirmPairing() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<PrintAgent, ApiError, { code: string; name: string; via: 'link' | 'code' }>({
    mutationFn: ({ code, name }) => confirmPairing(code, name),
    onSuccess: (_agent, { via }) => track('panel.print_agent_paired', { via }),
    onSettled: settle,
  });
}

/** O nível que a tela mostrou ao dono: vai no evento, e não o recalculado depois. */
export type PromptedUpdateLevel = 'recommended' | 'mandatory';

/**
 * Pede a atualização do computador (aviso, diálogo e lista de computadores). A resposta
 * entra no cache na hora, para o aviso e o diálogo fecharem sem esperar a releitura; o
 * evento sai só com `outcome` `created` ou `advanced`, com o modo efetivo devolvido.
 */
export function useRequestAgentUpdate() {
  const { queryClient, track, settle } = usePrintingMutationBase();
  return useMutation<
    AgentUpdateRequestResponse,
    ApiError,
    { agentId: string; mode: AgentUpdateMode; level: PromptedUpdateLevel }
  >({
    mutationFn: ({ agentId, mode }) => requestAgentUpdate(agentId, mode),
    onSuccess: (result, { level }) => {
      const tenantId = currentTenantId();
      if (tenantId) {
        queryClient.setQueryData<AgentRelease>(printingKeys.agentRelease(tenantId), (current) =>
          current
            ? {
                ...current,
                agents: current.agents.map((agent) =>
                  agent.agentId === result.request.agentId ? { ...agent, updateRequest: result.request } : agent,
                ),
              }
            : current,
        );
      }
      if (result.outcome === 'created' || result.outcome === 'advanced') {
        track('panel.print_agent_update_requested', { mode: result.request.mode, level });
      }
    },
    onSettled: settle,
  });
}

/**
 * "Testar conexão": não grava nada e não emite evento. Um 409
 * `printing.platform_disabled` só vira a mensagem; a faixa e a aba passam ao modo
 * leitura na próxima releitura do estado.
 */
export function useConnectionTest() {
  return useMutation<ConnectionTestResponse, ApiError, string>({
    mutationFn: testPrinterConnection,
  });
}

export function useCreateNetworkPrinter() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<Printer, ApiError, CreateNetworkPrinterRequest>({
    mutationFn: createNetworkPrinter,
    onSuccess: () => track('panel.settings_saved', { section: 'printing' }),
    onSettled: settle,
  });
}

export function useUpdatePrinter() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<Printer, ApiError, { id: string; request: UpdatePrinterRequest }>({
    mutationFn: ({ id, request }) => updatePrinter(id, request),
    onSuccess: () => track('panel.settings_saved', { section: 'printing' }),
    onSettled: settle,
  });
}

export function useDeletePrinter() {
  const { settle } = usePrintingMutationBase();
  return useMutation<void, ApiError, string>({
    mutationFn: deletePrinter,
    onSettled: settle,
  });
}

/** Um cupom cuja impressora ou vias mudaram na gravação: vira `panel.printer_assigned`. */
export interface AssignedTicketChange {
  ticketKind: TicketKind;
  copies: number;
  transport: PrinterTransport;
  width: PrinterWidth;
}

export function useSaveAssignments() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<
    PrintingAssignments,
    ApiError,
    { request: SaveAssignmentsRequest; changes: AssignedTicketChange[] }
  >({
    mutationFn: ({ request }) => saveAssignments(request),
    onSuccess: (_result, { changes }) => {
      for (const change of changes) {
        track('panel.printer_assigned', {
          ticket_kind: change.ticketKind,
          copies: change.copies,
          transport: change.transport,
          width: change.width,
        });
      }
    },
    onSettled: settle,
  });
}

export function useSendTestPrint() {
  const { track, settle } = usePrintingMutationBase();
  return useMutation<TestPrintResponse, ApiError, { printer: Printer; idempotencyKey: string }>({
    mutationFn: ({ printer, idempotencyKey }) => sendTestPrint(printer.id, idempotencyKey),
    onSuccess: (_result, { printer }) =>
      track('panel.test_print_sent', { transport: printer.transport, width: printer.width }),
    onSettled: settle,
  });
}

export function useReprintOrder() {
  const { queryClient, track } = usePrintingMutationBase();
  return useMutation<
    ReprintResponse,
    ApiError,
    {
      orderId: string;
      choice: TicketChoice;
      idempotencyKey: string;
      source: 'queue' | 'history';
      orderStatus: string;
    }
  >({
    mutationFn: ({ orderId, choice, idempotencyKey }) => reprintOrder(orderId, choice, idempotencyKey),
    onSuccess: (_result, { choice, source, orderStatus }) =>
      track('panel.order_reprinted', { tickets: choice.tickets, source, order_status: orderStatus }),
    onSettled: (_result, error, { orderId }) => {
      const tenantId = currentTenantId();
      if (!tenantId) return;
      void queryClient.invalidateQueries({ queryKey: printingKeys.orderJobs(tenantId, orderId) });
      void queryClient.invalidateQueries({ queryKey: ordersKeys.root(tenantId) });
      // Recusa pelo estado da impressão: a faixa e o bloco releem.
      if (
        isPrintingError(error, PRINTING_ERRORS.platformDisabled) ||
        isPrintingError(error, PRINTING_ERRORS.paused) ||
        isPrintingError(error, PRINTING_ERRORS.notActive)
      ) {
        void queryClient.invalidateQueries({ queryKey: printingKeys.status(tenantId) });
      }
    },
  });
}
